import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  EditButton,
  DeleteButton,
  Show,
  SimpleShowLayout,
  Edit,
  SimpleForm,
  SelectInput,
  TextInput,
  TopToolbar,
  ExportButton,
  useRecordContext,
  Filter,
  SearchInput,
  SimpleList,
  FunctionField,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
} from '@mui/material';

// 문의 유형 선택 옵션
const INQUIRY_TYPES = [
  { id: 'VENDOR', name: '입점 문의' },
  { id: 'PARTNERSHIP', name: '제휴 문의' },
];

// 상태 선택 옵션
const STATUS_CHOICES = [
  { id: 'PENDING', name: '대기' },
  { id: 'IN_PROGRESS', name: '처리중' },
  { id: 'COMPLETED', name: '완료' },
];

// 상태 라벨 매핑
const STATUS_LABELS: Record<string, string> = {
  PENDING: '대기',
  IN_PROGRESS: '처리중',
  COMPLETED: '완료',
};

// 상태 색상 매핑
const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'default'> = {
  PENDING: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
};

// 문의 유형 라벨 매핑
const TYPE_LABELS: Record<string, string> = {
  VENDOR: '입점 문의',
  PARTNERSHIP: '제휴 문의',
};

// 상태 필드 컴포넌트
const StatusField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const status = record.status;
  const label = STATUS_LABELS[status] || status;
  const color = STATUS_COLORS[status] || 'default';

  return (
    <Chip
      label={label}
      color={color}
      size="small"
      variant="filled"
    />
  );
};

// 문의 유형 필드 컴포넌트
const InquiryTypeField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const type = record.inquiryType;
  const label = TYPE_LABELS[type] || type;

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      color={type === 'VENDOR' ? 'primary' : 'secondary'}
    />
  );
};

// 필터
const PartnershipInquiryFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn placeholder="이름, 이메일 검색" />
    <SelectInput source="status" label="상태" choices={STATUS_CHOICES} />
    <SelectInput source="inquiryType" label="문의 유형" choices={INQUIRY_TYPES} />
  </Filter>
);

// 목록 액션
const ListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

// 입점/제휴 문의 목록
export const PartnershipInquiryList = () => {
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E' }}>
          입점/제휴 문의
        </Typography>
      </Box>
      <List
        title="입점/제휴 문의"
        filters={<PartnershipInquiryFilter />}
        actions={<ListActions />}
        sort={{ field: 'createdAt', order: 'DESC' }}
        perPage={25}
      >
        {isSmall ? (
          <SimpleList
            primaryText={(record) => record.name}
            secondaryText={(record) => (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <Chip
                  label={TYPE_LABELS[record.inquiryType] || record.inquiryType}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={STATUS_LABELS[record.status] || record.status}
                  size="small"
                  color={STATUS_COLORS[record.status] || 'default'}
                />
              </Box>
            )}
            tertiaryText={(record) => record.email}
            linkType="show"
          />
        ) : (
          <Datagrid rowClick="show">
            <FunctionField
              label="문의 유형"
              render={() => <InquiryTypeField />}
            />
            <TextField source="name" label="담당자명" />
            <TextField source="companyName" label="회사명" emptyText="-" />
            <TextField source="email" label="이메일" />
            <TextField source="phone" label="연락처" />
            <FunctionField
              label="상태"
              render={() => <StatusField />}
            />
            <DateField source="createdAt" label="접수일" />
            <EditButton />
            <DeleteButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

// 입점/제휴 문의 상세
export const PartnershipInquiryShow = () => (
  <Show title="입점/제휴 문의 상세">
    <SimpleShowLayout>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>문의 정보</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">문의 유형</Typography>
              <FunctionField render={() => <InquiryTypeField />} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">상태</Typography>
              <FunctionField render={() => <StatusField />} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">담당자명</Typography>
              <TextField source="name" />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">회사명</Typography>
              <TextField source="companyName" emptyText="-" />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">이메일</Typography>
              <TextField source="email" />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">연락처</Typography>
              <TextField source="phone" />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">접수일</Typography>
              <DateField source="createdAt" showTime />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>문의 내용</Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField source="content" sx={{ whiteSpace: 'pre-wrap' }} />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>관리자 메모</Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField source="adminNote" emptyText="메모가 없습니다" sx={{ whiteSpace: 'pre-wrap' }} />
        </CardContent>
      </Card>
    </SimpleShowLayout>
  </Show>
);

// 입점/제휴 문의 수정 (상태 변경, 메모 추가)
export const PartnershipInquiryEdit = () => (
  <Edit title="입점/제휴 문의 처리">
    <SimpleForm>
      <Box sx={{ width: '100%', maxWidth: 800 }}>
        {/* 읽기 전용 정보 */}
        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>문의 정보</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">담당자명</Typography>
                <TextField source="name" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">회사명</Typography>
                <TextField source="companyName" emptyText="-" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">이메일</Typography>
                <TextField source="email" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">연락처</Typography>
                <TextField source="phone" />
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">문의 내용</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <TextField source="content" />
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* 수정 가능 필드 */}
        <SelectInput
          source="status"
          label="처리 상태"
          choices={STATUS_CHOICES}
          fullWidth
        />
        <TextInput
          source="adminNote"
          label="관리자 메모"
          fullWidth
          multiline
          rows={4}
          helperText="내부 메모용 (고객에게 보이지 않음)"
        />
      </Box>
    </SimpleForm>
  </Edit>
);
